import React, { useMemo, useState } from "react";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";
import { appBareUrl } from "../utils/links";

export const MIN_AMOUNT_CENTS = 100;    // matches MIN_AMOUNT_CENTS in appearances.js (Stripe's floor)
export const MAX_AMOUNT_CENTS = 50000;  // matches MAX_SHARE_CENTS in guestShare.js
const MAX_NOTE_LENGTH = 300;
const PRESETS_CENTS = [1000, 2500, 5000, 10000];

export const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// The operator's vocabulary, not the member's: an admin needs to see exactly
// which state the money is in, including the ones we hide from the guest.
const STATUS_META = {
  offered: { label: "Booked", className: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300" },
  confirmed: { label: "Showed up", className: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300" },
  processing: { label: "Sending", className: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300" },
  paid: { label: "Paid", className: "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300" },
  cancelled: { label: "Cancelled", className: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300" },
  no_show: { label: "No-show", className: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300" },
  needs_review: { label: "Needs review", className: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300" },
};

/**
 * What "showed up" means on this row right now. Before the night is split it
 * is a person waiting on a number; after, it is money that either pays itself
 * (daily run on), waits for Pay now (daily run off), or is a settled nothing.
 *
 * `autoPay` is undefined when the budget call failed, and then we say neither
 * automatic nor manual: guessing is the same lie in one direction or the other.
 */
export function confirmedLine(appearance, autoPay, nowMs = Date.now()) {
  if (!appearance || !appearance.settled) {
    return "Showed up. Their share is worked out once the event is complete.";
  }
  if (appearance.belowMinimum) {
    return "Showed up. The night fell under the minimum share, so nothing is sent.";
  }
  if (!(appearance.amountCents > 0)) {
    return "Showed up. Share set to $0, so nothing is sent.";
  }
  if (autoPay && autoPay.paused) {
    return "Share set. Guest payouts are paused platform-wide, so nothing is sent until that is cleared.";
  }
  if (!autoPay || autoPay.enabled !== true) {
    if (!autoPay) return "Share set. Waiting to be paid.";
    return "Share set. Automatic payouts are off, so this is only paid when someone presses Pay now.";
  }
  const maturesMs = appearance.maturesAt ? Date.parse(appearance.maturesAt) : NaN;
  if (!Number.isFinite(maturesMs)) return "Share set. Pays out automatically once it matures.";
  if (maturesMs <= nowMs) return "Share set. Pays out in the next daily run.";
  const day = new Date(maturesMs).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `Share set. Pays out automatically on ${day}.`;
}

/** The toast after "They showed up": what to do next, which depends on whether the night is already split. */
export function confirmToast(settlement) {
  if (settlement && settlement.settled) return "Confirmed. Split again to give them a share.";
  return "Confirmed. Work out shares once the event is complete.";
}

/** The one-line status of a night, for the list header. Null when there is nothing to say. */
export function settlementLine(settlement) {
  if (!settlement) return null;
  if (settlement.locked) return "Shares are locked: a payout for this night has started.";
  if (settlement.belowMinimum) return "This night fell under the minimum share. Nobody is paid for it.";
  if (settlement.needsResettle) return "Someone was confirmed after the split. Split again to include them. That clears any adjustments.";
  if (settlement.settled) return null;
  if (!settlement.eventComplete) return "Shares are worked out once the event is complete and its revenue is final.";
  if (settlement.confirmedCount === 0) return "Confirm who showed up, then work out shares.";
  return null;
}

function Avatar({ name, photoURL, size = "w-10 h-10", onExpand }) {
  const face = photoURL ? (
    <img
      src={variantURL(photoURL, 200)}
      onError={fallbackToOriginal(photoURL)}
      alt={name || "Member"}
      loading="lazy"
      className={`${size} rounded-full object-cover shrink-0`}
    />
  ) : (
    <div className={`${size} rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0`}>
      <span className="text-gray-500 dark:text-gray-400">{(name || "?").charAt(0).toUpperCase()}</span>
    </div>
  );
  // The thumbnail is a 200px variant, so an operator checking a face needs the
  // full photo. Only clickable where a handler is passed; the dialogs show the
  // same avatar with nowhere to open it.
  if (!onExpand) return face;
  return (
    <button
      type="button"
      onClick={onExpand}
      title="View photo"
      className="shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-primary"
    >
      {face}
    </button>
  );
}

/** A percent, shown the way an operator writes it: 10%, not 0.1. */
const pct = (rate) => `${Math.round((rate || 0) * 1000) / 10}%`;

// Where a rate came from, in the operator's words. Shown next to the number so
// nobody edits the wrong level: changing the platform default will not move an
// event that has its own rate.
const RATE_SOURCE_LABEL = {
  event: "set for this event",
  host: "this host's rate",
  global: "platform default",
  default: "built-in default",
};

/**
 * This event's guest pool: what it has taken, what the host is owed, and what
 * is therefore left to share among the guests who show up.
 *
 * Before the night the numbers are "so far" and the copy says so; after the
 * split they are the numbers the shares came from.
 */
export function GuestBudget({ pool, settlement, canEdit, isSaving, onSetRate }) {
  if (!pool) return null;
  const split = Boolean(settlement && settlement.settled);
  const rows = [
    { label: split ? "Taken" : "Taken so far", value: money(pool.revenueCents) },
    { label: "Host's share", value: money(pool.hostCents) },
    { label: `Guest pool (${pct(pool.rate)})`, value: money(pool.poolCents) },
    { label: split ? "Shared out" : "Booked", value: split ? money(pool.committedCents) : String(pool.committedCount || 0) },
  ];
  const over = split && pool.overCommittedCents > 0;

  return (
    <div className="mb-3 p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Guest pool</h4>
        <span className={`text-sm font-semibold ${over ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-gray-100"}`}>
          {over
            ? `${money(pool.overCommittedCents)} over the pool`
            : split
              ? `${money(pool.committedCents)} of ${money(pool.poolCents)} shared`
              : `${money(pool.poolCents)} to share so far`}
        </span>
      </div>

      <dl className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {rows.map((r) => (
          <div key={r.label} className="min-w-0">
            <dt className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">{r.label}</dt>
            <dd className="text-sm text-gray-900 dark:text-gray-100">{r.value}</dd>
          </div>
        ))}
      </dl>

      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        The host is paid first and Gist keeps at least {pct(pool.platformFloorRate)}. What is left,
        up to {pct(pool.maxRate)} of the event, is the guest pool. Once the event is complete it is
        split equally among the guests who showed up, and you can adjust a share before paying.
        {pool.cappedBy === "platform_floor" && " This event's pool is smaller than its rate because the host's share leaves less room."}
      </p>

      {canEdit && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label htmlFor="guest-share-rate" className="text-xs text-gray-600 dark:text-gray-300">
            Share for this event
          </label>
          <select
            id="guest-share-rate"
            value={pool.rateSource === "event" ? String(pool.rate) : "inherit"}
            disabled={isSaving}
            onChange={(e) => onSetRate(e.target.value === "inherit" ? null : Number(e.target.value))}
            className="px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-xs focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
          >
            <option value="inherit">Inherit ({pct(pool.rate)})</option>
            {[0, 0.05, 0.1, 0.15, 0.2, 0.25].map((r) => (
              <option key={r} value={r}>{pct(r)}</option>
            ))}
          </select>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {RATE_SOURCE_LABEL[pool.rateSource] || ""}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Book a guest for this event. No amount: the share is worked out after the
 * night from what it made. The preview is what the server sends, word for word.
 */
export function BookGuestDialog({ guest, eventName, isSaving, onCancel, onConfirm }) {
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);

  const trimmedNote = note.trim();
  const preview = trimmedNote
    ? `${trimmedNote}\n\nSee it at ${appBareUrl("/profile")}`
    : `${(guest?.name || "").trim().split(/\s+/)[0] || "They"}, we'd love you at ${eventName || "an event"} on Gist. Show up and you'll earn a share of what the night makes. Details in your profile: ${appBareUrl("/profile")}`;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4" role="dialog" aria-modal="true">
      <div className="w-full sm:max-w-md max-h-[92dvh] overflow-y-auto rounded-t-2xl sm:rounded-lg bg-white dark:bg-gray-900 p-4 sm:p-5 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <Avatar name={guest?.name} photoURL={guest?.photoURL} size="w-12 h-12" />
          <div className="min-w-0">
            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {guest?.name || "Unnamed member"}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              Book them to show up to {eventName || "this event"}
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-300">
          They earn a share of what the night makes. The number is worked out once the event is
          complete, split among the guests who showed up.
        </p>

        <label htmlFor="offer-note" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mt-4 mb-1">
          Message (optional)
        </label>
        <textarea
          id="offer-note"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE_LENGTH))}
          disabled={isSaving}
          placeholder="Leave blank to send the standard note."
          className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
        <div className="flex justify-end mt-1 text-xs text-gray-500 dark:text-gray-400">
          {note.length}/{MAX_NOTE_LENGTH}
        </div>

        <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 dark:text-gray-200">
          <input
            type="checkbox"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            disabled={isSaving}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          Text them now
        </label>
        {notify && (
          <pre className="mt-2 p-2 rounded-md bg-gray-50 dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans">
            {preview}
          </pre>
        )}

        <div className="flex gap-2 mt-4">
          <button
            type="button"
            onClick={() => onConfirm({ note: trimmedNote, notify })}
            disabled={isSaving}
            className="flex-1 sm:flex-none px-4 py-2 rounded-md font-medium bg-primary text-white hover:bg-primaryDark transition-colors disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Book them"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="flex-1 sm:flex-none px-4 py-2 rounded-md font-medium bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Move one guest's share by hand, after the split. Amount is entered in
 * DOLLARS and converted once, here, to the whole cents the callable requires.
 * $0 is a real choice; anything between $0 and $1 is not payable.
 */
export function AdjustShareDialog({ appearance, pool, othersCents, isSaving, onCancel, onConfirm }) {
  const [dollars, setDollars] = useState(String(((appearance?.amountCents || 0) / 100).toFixed(2)));

  const cents = useMemo(() => {
    const n = Number(dollars);
    if (!Number.isFinite(n)) return NaN;
    return Math.round(n * 100);
  }, [dollars]);

  const invalid = !Number.isFinite(cents) || cents < 0 || (cents !== 0 && cents < MIN_AMOUNT_CENTS) || cents > MAX_AMOUNT_CENTS;
  const total = Number.isFinite(cents) ? (othersCents || 0) + cents : null;
  const overBy = total != null && pool ? total - pool.poolCents : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4" role="dialog" aria-modal="true">
      <div className="w-full sm:max-w-md max-h-[92dvh] overflow-y-auto rounded-t-2xl sm:rounded-lg bg-white dark:bg-gray-900 p-4 sm:p-5 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <Avatar name={appearance?.name} photoURL={appearance?.photoURL} size="w-12 h-12" />
          <div className="min-w-0">
            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {appearance?.name || "Unnamed member"}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              Adjust their share of {appearance?.eventName || "this event"}
            </div>
          </div>
        </div>

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Their share</label>
        <div className="grid grid-cols-4 gap-2 mb-2">
          {PRESETS_CENTS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => setDollars(String(preset / 100))}
              className={`px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${cents === preset
                ? "bg-primary text-white"
                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
            >
              {money(preset)}
            </button>
          ))}
        </div>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
          <input
            type="number"
            min="0"
            max="500"
            step="1"
            inputMode="decimal"
            value={dollars}
            onChange={(e) => setDollars(e.target.value)}
            disabled={isSaving}
            aria-label="Share in dollars"
            className="w-full pl-7 pr-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
          />
        </div>
        {invalid && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
            Enter $0, or an amount between {money(MIN_AMOUNT_CENTS)} and {money(MAX_AMOUNT_CENTS)}.
          </p>
        )}

        {pool && total != null && (
          <p className={`mt-1 text-xs ${overBy > 0 ? "text-amber-700 dark:text-amber-400" : "text-gray-500 dark:text-gray-400"}`}>
            {overBy > 0
              ? `This puts the night at ${money(total)}, ${money(overBy)} over its ${money(pool.poolCents)} pool.`
              : `The night comes to ${money(total)} of its ${money(pool.poolCents)} pool.`}
          </p>
        )}

        <div className="flex gap-2 mt-4">
          <button
            type="button"
            onClick={() => onConfirm({ amountCents: cents })}
            disabled={invalid || isSaving}
            className="flex-1 sm:flex-none px-4 py-2 rounded-md font-medium bg-primary text-white hover:bg-primaryDark transition-colors disabled:opacity-50"
          >
            {isSaving ? "Saving..." : `Set to ${Number.isFinite(cents) ? money(cents) : ""}`}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="flex-1 sm:flex-none px-4 py-2 rounded-md font-medium bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** The amount cell: a number only once the night is split, never a fake $0.00 before it. */
function amountCell(appearance) {
  if (!appearance.settled) {
    return <span className="text-sm text-gray-500 dark:text-gray-400">{appearance.status === "offered" ? "share tbd" : "share pending"}</span>;
  }
  return (
    <span className="font-semibold text-gray-900 dark:text-gray-100">
      {money(appearance.amountCents)}
      {appearance.shareAdjusted && <span className="ml-1 text-[10px] font-normal uppercase tracking-wide text-gray-500 dark:text-gray-400">adjusted</span>}
      {appearance.belowMinimum && <span className="ml-1 text-[10px] font-normal uppercase tracking-wide text-gray-500 dark:text-gray-400">under minimum</span>}
    </span>
  );
}

/** One booked guest: who, where the money is, and what to do next. */
function AppearanceRow({ appearance, checkedIn, busy, canPay, autoPay, onAction, onExpandPhoto }) {
  const meta = STATUS_META[appearance.status] || STATUS_META.offered;
  const isOffered = appearance.status === "offered";
  const isConfirmed = appearance.status === "confirmed";
  const isPaid = appearance.status === "paid";
  const isSending = appearance.status === "processing";
  const needsReview = appearance.status === "needs_review";
  const payable = isConfirmed && appearance.settled && appearance.amountCents > 0;
  // Payable but unpayable — the operator needs the reason on the row, not
  // after a failed Pay now.
  const blocked = payable && !appearance.stripeReady;

  return (
    <div className="p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Avatar
            name={appearance.name}
            photoURL={appearance.photoURL}
            onExpand={onExpandPhoto ? () => onExpandPhoto(appearance) : undefined}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {appearance.name || "Unnamed member"}
              </span>
              {amountCell(appearance)}
              <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${meta.className}`}>
                {meta.label}
              </span>
              {checkedIn && !isPaid && (
                <span
                  title="They are in the event's check-in list. That is written by the member, so it is a hint, not proof."
                  className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                >
                  Checked in
                </span>
              )}
              {/* Same class of hint as "Checked in": the person confirming
                  should look twice at who actually turned up. */}
              {appearance.photoChangedSinceOffer && !isPaid && (
                <span
                  title="Their profile photo changed after this booking. The face on the profile now is not the one you booked."
                  className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300"
                >
                  Photo changed
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {isPaid && appearance.paidAt
                ? `Paid ${new Date(appearance.paidAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
                : blocked
                  ? "Waiting on their Stripe setup. They can't be paid yet."
                  : needsReview
                    ? appearance.reviewNote || "Payout needs manual reconciliation"
                    : isConfirmed
                      ? confirmedLine(appearance, autoPay)
                      : isOffered
                        ? appearance.notifiedAt ? "Booked and texted" : "Booked, not yet texted"
                        : ""}
            </div>
            {isConfirmed && appearance.settled && appearance.shareNotifiedAt && appearance.shareNotifiedCents === appearance.amountCents && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Told their share.</div>
            )}
            {appearance.lastError && !isPaid && (
              <div className="text-xs text-red-600 dark:text-red-400 mt-0.5">Last attempt: {appearance.lastError}</div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end shrink-0">
          {isSending && <span className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400">Sending...</span>}
          {isPaid && <span className="px-3 py-1.5 text-sm text-green-700 dark:text-green-400">Paid</span>}

          {isOffered && (
            <button
              type="button"
              onClick={() => onAction(appearance, "confirm")}
              disabled={Boolean(busy)}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-white hover:bg-primaryDark transition-colors disabled:opacity-50"
            >
              {busy === "confirm" ? "Saving..." : "They showed up"}
            </button>
          )}
          {payable && canPay && appearance.stripeReady && (
            <button
              type="button"
              onClick={() => onAction(appearance, "pay")}
              disabled={Boolean(busy)}
              title="Send the money now, without waiting for the daily run"
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-white hover:bg-primaryDark transition-colors disabled:opacity-50"
            >
              {busy === "pay" ? "Sending..." : "Pay now"}
            </button>
          )}
          {needsReview && (
            // A deliberate dead end. This appearance reached needs_review because
            // we could not prove the transfer failed AND Stripe's idempotency
            // window has closed, so any retry from here risks paying twice. It is
            // resolved in the Stripe dashboard, by a person.
            <span className="px-3 py-1.5 text-sm text-red-700 dark:text-red-400">
              Reconcile in Stripe
            </span>
          )}
          {isConfirmed && appearance.settled && canPay && (
            <button
              type="button"
              onClick={() => onAction(appearance, "adjust")}
              disabled={Boolean(busy)}
              title="Move this one share up or down before paying"
              className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              Adjust share
            </button>
          )}
          {isOffered && (
            <button
              type="button"
              onClick={() => onAction(appearance, "no_show")}
              disabled={Boolean(busy)}
              className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {busy === "no_show" ? "Saving..." : "No-show"}
            </button>
          )}
          {(isOffered || isConfirmed) && (
            <button
              type="button"
              onClick={() => onAction(appearance, "cancel")}
              disabled={Boolean(busy)}
              className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {busy === "cancel" ? "Saving..." : "Cancel"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Everyone booked for this event, with where the night stands and the two
 * night-level actions: work out the shares, and text them out.
 */
export function GuestAppearancesList({
  appearances, totals, settlement, checkedInIds, busyById, listBusy, canPay, autoPay, onAction, onSettle, onNotify,
  onExpandPhoto,
}) {
  const live = appearances.filter((a) => a.status !== "cancelled" && a.status !== "no_show");
  const closed = appearances.filter((a) => a.status === "cancelled" || a.status === "no_show");
  const [showClosed, setShowClosed] = useState(false);

  if (appearances.length === 0) return null;

  const line = settlementLine(settlement);
  const showSettle = canPay && settlement && settlement.canSettle;
  const showNotify = canPay && settlement && settlement.unnotifiedCount > 0;

  return (
    <div className="mb-5">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Guests booked for this event
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {settlement && settlement.settled
            ? `${money(totals?.sharedOutCents || 0)} shared · ${money(totals?.paidCents || 0)} paid`
            : `${live.length} booked`}
        </span>
      </div>

      {!canPay && live.length > 0 && (
        <p className="mb-2 text-xs text-amber-700 dark:text-amber-400">
          You can confirm and cancel these. Booking, splitting the night and paying need a
          super-admin account with the signed claim, like the host payout screens.
        </p>
      )}

      {(line || showSettle || showNotify) && (
        <div className="mb-2 flex flex-col sm:flex-row sm:items-center gap-2">
          {line && <p className="text-xs text-gray-600 dark:text-gray-300 flex-1">{line}</p>}
          <div className="flex gap-2 shrink-0">
            {showSettle && (
              <button
                type="button"
                onClick={onSettle}
                disabled={Boolean(listBusy)}
                title="Split this event's guest pool equally among everyone confirmed"
                className="px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-white hover:bg-primaryDark transition-colors disabled:opacity-50"
              >
                {listBusy === "settle" ? "Working it out..." : settlement.settled || settlement.needsResettle ? "Split again" : "Work out shares"}
              </button>
            )}
            {showNotify && (
              <button
                type="button"
                onClick={onNotify}
                disabled={Boolean(listBusy)}
                title="Text each guest what their share came to"
                className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                {listBusy === "notify" ? "Texting..." : `Text ${settlement.unnotifiedCount} their share`}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {live.map((a) => (
          <AppearanceRow
            key={a.appearanceId}
            appearance={a}
            checkedIn={checkedInIds?.has(a.userID)}
            busy={busyById?.[a.appearanceId]}
            canPay={canPay}
            autoPay={autoPay}
            onAction={onAction}
            onExpandPhoto={onExpandPhoto}
          />
        ))}
      </div>

      {closed.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setShowClosed((v) => !v)}
            className="mt-2 text-xs underline text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            {showClosed ? "Hide" : `Show ${closed.length} cancelled or no-show`}
          </button>
          {showClosed && (
            <div className="flex flex-col gap-2 mt-2 opacity-70">
              {closed.map((a) => (
                <AppearanceRow
                  key={a.appearanceId}
                  appearance={a}
                  checkedIn={false}
                  busy={busyById?.[a.appearanceId]}
                  canPay={canPay}
                  autoPay={autoPay}
                  onAction={onAction}
                  onExpandPhoto={onExpandPhoto}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
